import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Play, Calendar, BookOpen, User } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import UniversalBottomNav from './navigation/UniversalBottomNav';
import { markTap } from '../utils/tabPerf';

// Module-scope constant — lifted out of the component so the resolveTabId
// useCallback can list it in its dep array without churning every render.
const VISITOR_TABS = [
  { id: 'home', label: 'Accueil', icon: Home, path: '/' },
  { id: 'events', label: 'Événements', icon: Calendar, path: '/events' },
  { id: 'watch', label: 'Regarder', icon: Play, path: '/watch' },
  { id: 'library', label: 'Librairie', icon: BookOpen, path: '/library' },
  { id: 'profile', label: 'Profil', icon: User, path: '/visitor/profile' },
];

/**
 * BottomNav - Smart bottom navigation for public/visitor screens
 *
 * Visitors (auth'd or not) → 5 flat tabs (no Plus menu): Accueil, Événements,
 * Regarder, Librairie, Profil.
 * Authenticated members/leaders/dg/admin → UniversalBottomNav w/ their role.
 */
const BottomNav = React.memo(() => {
  const { isAuthenticated, activeRole } = useAuth();

  // Dev-only sanity log — if the navbar ever renders with the wrong role
  // again, this shows up in the console with a timestamp. Safe in prod
  // (process.env check stripped at build time).
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug(
      `[BottomNav] ${new Date().toISOString()} isAuth=${isAuthenticated} activeRole=${activeRole}`
    );
  }

  // Visitor role (auth'd or not) gets the clean 5-tab nav
  if (!isAuthenticated || activeRole === 'visitor') {
    return <VisitorBottomNav />;
  }

  return <UniversalBottomNav role={activeRole || 'member'} />;
});

/**
 * VisitorBottomNav — 5 flat tabs, no Plus menu
 * Accueil | Événements | Regarder | Librairie | Profil
 */
const VisitorBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();

  const tabs = VISITOR_TABS;

  // Path → tab id resolver. Used to seed and re-sync the optimistic state
  // when the URL changes from outside the navbar (back/forward, deep links).
  const resolveTabId = useCallback((pathname) => {
    // Match more specific paths before '/' so that any non-home path wins.
    for (const t of VISITOR_TABS) {
      if (t.path === '/') continue;
      if (pathname === t.path || pathname.startsWith(t.path + '/')) return t.id;
    }
    if (pathname === '/') return 'home';
    return null;
  }, []);

  // Optimistic active-tab state — set immediately on tap so the indicator
  // moves on the next paint, before React Router commits the navigation.
  const [activeId, setActiveId] = useState(() => resolveTabId(location.pathname));
  useEffect(() => {
    const next = resolveTabId(location.pathname);
    setActiveId(prev => (prev === next ? prev : next));
  }, [location.pathname, resolveTabId]);

  const handleTap = (tab) => {
    setActiveId(tab.id);
    if (location.pathname === tab.path) {
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}
    } else {
      markTap(tab.path);
      navigate(tab.path);
    }
  };

  return (
    <nav
      className={`pcc-bottom-nav transition-colors duration-200 ${
        isDark
          ? 'bg-[#2D2D2D] border-t border-[#444444]'
          : 'bg-white border-t border-gray-100'
      }`}
      style={{
        // Inline styles override any Tailwind class ordering / specificity edge
        // cases. These five properties are the bulletproof anchor for iOS/iPadOS.
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        zIndex: 9999,
        boxShadow: isDark ? '0 -4px 20px rgba(0, 0, 0, 0.3)' : '0 -4px 20px rgba(0, 0, 0, 0.08)',
        // Safe area for iPhone home indicator; iPads get the fallback 12px
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 12px)',
      }}
      data-testid="bottom-nav"
    >
      <div className="flex justify-around items-center h-16 max-w-lg lg:max-w-2xl mx-auto">
        {tabs.map((item) => {
          const Icon = item.icon;
          const active = activeId === item.id;
          return (
            <button
              key={item.id}
              onClick={() => handleTap(item)}
              data-testid={`nav-${item.id}`}
              className={`
                flex flex-col items-center justify-center flex-1 h-full
                transition-colors duration-150 ease-out relative
                ${active
                  ? 'text-[#9398DE]'
                  : isDark
                    ? 'text-[#6A6A6A] hover:text-[#9A9A9A]'
                    : 'text-gray-400 hover:text-gray-600'
                }
              `}
            >
              {/* Indicator rendered always so opacity transition can animate. */}
              <div
                aria-hidden
                className={`absolute top-0 left-1/2 -translate-x-1/2 w-8 h-1 bg-[#9398DE] rounded-b-full transition-opacity duration-150 ease-out ${active ? 'opacity-100' : 'opacity-0'}`}
              />
              <Icon
                size={22}
                className="mb-1"
                strokeWidth={active ? 2.5 : 2}
              />
              <span className={`text-xs tracking-wide ${active ? 'font-semibold' : 'font-medium'}`}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
